
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IControleProducao, IControleProducaoEmpresa } from '@/models/Entidades/Cadastros/PCP/IControleProducao';
import ServicoControleProducao from '@/servicos/Cadastros/PCP/ServicoControleProducao';
import { ETipoControleProducao } from '@/models/Enumeradores/Cadastros/PCP/ETipoControleProcucao';
import SelecionarTipoControleProducao from '@/components/PCP/SelecionarTipoControleProducao.vue';

export default defineComponent({
  name: 'ControleProducaoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarTipoControleProducao,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, defineEmpresasSelecionadasCadastroCompartilhado, apresentarMensagemAlerta,
      preencherPermissoesDados, filtrarPermissaoDadosUsuarioMultiEmpresas, apresentarMensagemSucesso, verificaConceitoParaApresentarEmpresas,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);
    const servicoControleProducao = new ServicoControleProducao();
    telaBase.identificadorRecurso = 'CADASTRO_CONTROLE_PRODUCAO';
    telaBase.identificadorPermissao = 'PER_CADASTRO_CONTROLE_PRODUCAO';

    const state = reactive({
      controleProducao: {} as IControleProducao,
      editavel: true,
      nome: '',
      tipoMedida: '',
      tipoControle: ETipoControleProducao.Outros,
    });

    function objetoInicial() {
      state.controleProducao = {} as IControleProducao;
      state.controleProducao.codigo = 0;
      state.controleProducao.ativo = true;
      state.controleProducao.empresas = [] as IControleProducaoEmpresa[];
      telaBase.empresasSelecionadas = [];
      state.editavel = true;
      state.nome = '';
      state.tipoMedida = '';
      state.tipoControle = ETipoControleProducao.Outros;
    }

    function preparaPersistenciaEmpresas() {
      const controleProducaoEmpresas : IControleProducaoEmpresa[] = [];
      if (telaBase.empresasSelecionadas.length > 0) {
        telaBase.empresasSelecionadas.forEach((codigoEmpresa) => {
          const empresaExistente = state.controleProducao.empresas.find((c) => c.codigoEmpresa === codigoEmpresa);
          if (empresaExistente !== undefined) {
            controleProducaoEmpresas.push(empresaExistente);
          } else {
            const controleProducaoEmpresa: IControleProducaoEmpresa = { codigo: 0, codigoControleProducao: state.controleProducao.codigo, codigoEmpresa };
            controleProducaoEmpresas.push(controleProducaoEmpresa);
          }
        });
      }
      state.controleProducao.empresas = controleProducaoEmpresas;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      if (!UtilitarioGeral.valorValido(state.controleProducao.nome)) {
        apresentarMensagemAlerta('É necessário informar a Descrição');
        return;
      }

      apresentarBarraProgresso();
      preparaPersistenciaEmpresas();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoControleProducao.incluir(state.controleProducao);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoControleProducao.alterar(state.controleProducao);
      }

      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          objetoInicial();
          telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
          defineNovaOperacao(props.operacao);
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);

        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.controleProducao = await servicoControleProducao.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          if (UtilitarioGeral.validaLista(state.controleProducao.empresas)) {
            state.controleProducao.empresas.forEach((e) => {
              telaBase.empresasSelecionadas.push(e.codigoEmpresa);
            });
          }
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      objetoInicial,
      EPermissaoDados,
    };
  },
});
